'use client';

import { useMemo, useCallback } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { labQueries } from '@/features/labs';
import type { OfficeBiLabel } from '@/features/configurations';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import { getSortingStateParser } from '@/lib/parsers';
import {
  Plus,
  FileText,
  Settings,
  Eye,
  Pencil,
  Copy,
  Trash2,
  ClipboardList,
  ToggleLeft,
  ToggleRight,
  Star,
  EllipsisVertical
} from 'lucide-react';

import { useDataTable } from '@/hooks/use-data-table';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import { PermissionGate } from '@/features/auth';
import { DYNAMIC_FORMS_HASH } from '../constants';
import { dynamicFormQueries } from '../services/dynamic-forms.queries';
import {
  useActivateDynamicFormMutation,
  useDeactivateDynamicFormMutation,
  useSetDefaultDynamicFormMutation,
  useUnsetDefaultDynamicFormMutation,
  useDeleteDynamicFormMutation,
  useDuplicateDynamicFormMutation
} from '../services/dynamic-forms.mutations';
import { DynamicFormMetrics } from './dynamic-form-metrics';
import { DynamicFormCard } from './dynamic-form-card';
import { DynamicFormCompactRow } from './dynamic-form-compact-row';
import { DeleteFormDialog } from './delete-form-dialog';
import { DynamicFormWizardDialog } from './dynamic-form-wizard-dialog';
import { DynamicFormPreviewDialog } from './dynamic-form-preview-dialog';
import { DynamicFormsTableActionBar } from './dynamic-forms-table-action-bar';
import type {
  DynamicForm,
  DynamicFormStatus,
  DynamicFormType,
  DynamicFormsListParams
} from '../types';

const typeTranslationKeys: Record<DynamicFormType, string> = {
  registration: 'typeRegistration',
  filling: 'typeFilling'
};

export function DynamicFormsPageContent() {
  const t = useTranslations('dynamicForms');
  const locale = useLocale();
  const { data: labsData } = useQuery(labQueries.all());
  const officeFilterOptions = useMemo(() => {
    const settings =
      (
        labsData as
          | { data?: { offices?: OfficeBiLabel[] }; offices?: OfficeBiLabel[] }
          | undefined
      )?.data ?? labsData;
    const raw = (settings as { offices?: OfficeBiLabel[] } | undefined)
      ?.offices;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((o) => o.status === 'active')
      .map((o) => ({
        value: o.ar,
        label: locale === 'ar' ? o.ar : (o.en ?? o.ar)
      }));
  }, [labsData, locale]);
  const [view, setView] = useDataView('dynamic-forms', 'grid');

  // Overlay open-state is driven by the URL hash so overlays are deep-linkable,
  // survive refresh, and close on browser Back. The wizard is shared between
  // create (`#create`) and edit (`#edit/<id>`); preview and delete are
  // row-scoped (`#preview/<id>`, `#delete/<id>`).
  const { open: createOpen, onOpenChange: toggleCreate } = useHashDialog(
    DYNAMIC_FORMS_HASH.create
  );
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(DYNAMIC_FORMS_HASH.edit);
  const {
    open: previewOpen,
    param: previewId,
    openWith: openPreview,
    onOpenChange: closePreview
  } = useHashParamDialog(DYNAMIC_FORMS_HASH.preview);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(DYNAMIC_FORMS_HASH.delete);

  const handleEdit = useCallback(
    (form: DynamicForm) => {
      openEdit(String(form.id));
    },
    [openEdit]
  );

  const handlePreview = useCallback(
    (form: DynamicForm) => {
      openPreview(String(form.id));
    },
    [openPreview]
  );

  const handleDeleteTrigger = useCallback(
    (form: DynamicForm) => {
      openDelete(String(form.id));
    },
    [openDelete]
  );

  const duplicateMutation = useDuplicateDynamicFormMutation();
  const activateMutation = useActivateDynamicFormMutation();
  const deactivateMutation = useDeactivateDynamicFormMutation();
  const setDefaultMutation = useSetDefaultDynamicFormMutation();
  const unsetDefaultMutation = useUnsetDefaultDynamicFormMutation();
  const deleteMutation = useDeleteDynamicFormMutation();

  const handleDuplicate = useCallback(
    async (form: DynamicForm) => {
      try {
        await duplicateMutation.mutateAsync(form.id);
      } catch {
        // mutation handles error
      }
    },
    [duplicateMutation]
  );

  const handleToggleStatus = useCallback(
    async (form: DynamicForm) => {
      try {
        if (form.status === 'active') {
          await deactivateMutation.mutateAsync(form.id);
        } else {
          await activateMutation.mutateAsync(form.id);
        }
      } catch {
        // handled by global MutationCache
      }
    },
    [activateMutation, deactivateMutation]
  );

  const handleToggleDefault = useCallback(
    async (form: DynamicForm) => {
      try {
        if (form.is_default) {
          await unsetDefaultMutation.mutateAsync(form.id);
        } else {
          await setDefaultMutation.mutateAsync(form.id);
        }
      } catch {
        // handled by global MutationCache
      }
    },
    [setDefaultMutation, unsetDefaultMutation]
  );

  const columns = useMemo<ColumnDef<DynamicForm>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('formName')} />
        ),
        cell: ({ row }) => (
          <div>
            <span className='font-medium'>{row.original.name}</span>
            <p className='text-muted-foreground text-xs'>
              {row.original.form_id_mask}
            </p>
          </div>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('searchPlaceholder'),
          variant: 'text' as const,
          placeholder: t('searchPlaceholder')
        }
      },
      {
        id: 'form_id_mask',
        accessorKey: 'form_id_mask',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('formIdMask')} />
        ),
        cell: ({ row }) => row.original.form_id_mask,
        enableSorting: false,
        meta: { label: t('formIdMask') }
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('formType')} />
        ),
        cell: ({ row }) => (
          <Badge variant='outline' className='gap-1'>
            <ClipboardList className='h-3 w-3' />
            {t(typeTranslationKeys[row.original.type])}
          </Badge>
        ),
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: true,
        meta: {
          label: t('filterByType'),
          variant: 'multiSelect' as const,
          icon: 'solar:clipboard-list-bold-duotone',
          options: [
            { value: 'registration', label: t('typeRegistration') },
            { value: 'filling', label: t('typeFilling') }
          ]
        }
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('status')} />
        ),
        cell: ({ row }) => (
          <Badge
            variant='default'
            className={cn(
              row.original.status === 'active'
                ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
                : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
            )}
          >
            {row.original.status === 'active'
              ? t('statusActive')
              : t('statusDraft')}
          </Badge>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('filterByStatus'),
          variant: 'multiSelect' as const,
          icon: 'solar:tag-bold-duotone',
          options: [
            { value: 'active', label: t('statusActive') },
            { value: 'draft', label: t('statusDraft') }
          ]
        }
      },
      {
        id: 'is_default',
        accessorKey: 'is_default',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('defaultOnly')} />
        ),
        cell: ({ row }) =>
          row.original.is_default ? (
            <Star className='h-4 w-4 fill-current text-amber-500' />
          ) : (
            <span className='text-muted-foreground'>—</span>
          ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('defaultOnly'),
          variant: 'multiSelect' as const,
          icon: 'solar:star-bold-duotone',
          options: [
            { value: '1', label: t('filterDefaultOnly') },
            { value: '0', label: t('filterDefaultExcluded') }
          ]
        }
      },
      {
        id: 'offices',
        accessorKey: 'offices',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('labs')} />
        ),
        cell: ({ row }) => {
          const offices = row.original.offices ?? [];
          if (offices.length === 0) {
            return (
              <span className='text-muted-foreground text-xs'>
                {t('labsGlobal')}
              </span>
            );
          }
          return (
            <div className='flex flex-wrap gap-1'>
              {offices.map((o) => {
                const opt = officeFilterOptions.find((x) => x.value === o);
                return (
                  <Badge key={o} variant='secondary' className='text-xs'>
                    {opt?.label ?? o}
                  </Badge>
                );
              })}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: officeFilterOptions.length > 0,
        meta: {
          label: t('filterByLabs'),
          variant: 'multiSelect' as const,
          icon: 'solar:buildings-2-bold-duotone',
          options: officeFilterOptions
        }
      },
      {
        id: 'fields_count',
        accessorFn: (row) => row.fields_count ?? row.fields?.length ?? 0,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('fields')} />
        ),
        cell: ({ row }) =>
          row.original.fields_count ?? row.original.fields?.length ?? 0,
        enableSorting: false,
        meta: { label: t('fields') }
      },
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('createdAt')} />
        ),
        cell: ({ row }) => {
          const date = row.original.created_at;
          if (!date) return <span className='text-muted-foreground'>-</span>;
          return (
            <span>
              {new Date(date).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </span>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('createdAtRange'),
          variant: 'dateRange' as const
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('actions')}</div>,
        cell: ({ row }) => {
          const form = row.original;
          return (
            <div className='flex justify-end'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                    <EllipsisVertical className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem onClick={() => handlePreview(form)}>
                    <Eye className='me-2 h-3.5 w-3.5' />
                    {t('preview')}
                  </DropdownMenuItem>
                  <PermissionGate permissions={['dynamic_forms.edit']}>
                    <DropdownMenuItem onClick={() => handleEdit(form)}>
                      <Pencil className='me-2 h-3.5 w-3.5' />
                      {t('edit')}
                    </DropdownMenuItem>
                  </PermissionGate>
                  <PermissionGate permissions={['dynamic_forms.create']}>
                    <DropdownMenuItem
                      onClick={() => handleDuplicate(form)}
                      disabled={duplicateMutation.isPending}
                    >
                      <Copy className='me-2 h-3.5 w-3.5' />
                      {t('duplicate')}
                    </DropdownMenuItem>
                  </PermissionGate>
                  <DropdownMenuSeparator />
                  <PermissionGate permissions={['dynamic_forms.edit']}>
                    <DropdownMenuItem onClick={() => handleToggleStatus(form)}>
                      {form.status === 'active' ? (
                        <ToggleRight className='me-2 h-3.5 w-3.5' />
                      ) : (
                        <ToggleLeft className='me-2 h-3.5 w-3.5' />
                      )}
                      {form.status === 'active'
                        ? t('deactivate')
                        : t('activate')}
                    </DropdownMenuItem>
                  </PermissionGate>
                  <PermissionGate permissions={['dynamic_forms.edit']}>
                    <DropdownMenuItem onClick={() => handleToggleDefault(form)}>
                      <Star
                        className={cn(
                          'me-2 h-3.5 w-3.5',
                          form.is_default && 'fill-current'
                        )}
                      />
                      {form.is_default ? t('unsetDefault') : t('setDefault')}
                    </DropdownMenuItem>
                  </PermissionGate>
                  <DropdownMenuSeparator />
                  <PermissionGate permissions={['dynamic_forms.delete']}>
                    <DropdownMenuItem
                      className='text-destructive focus:text-destructive'
                      onClick={() => handleDeleteTrigger(form)}
                    >
                      <Trash2 className='me-2 h-3.5 w-3.5' />
                      {t('delete')}
                    </DropdownMenuItem>
                  </PermissionGate>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    [
      t,
      handleEdit,
      handlePreview,
      handleDeleteTrigger,
      handleDuplicate,
      handleToggleStatus,
      handleToggleDefault,
      duplicateMutation.isPending,
      officeFilterOptions
    ]
  );

  // URL state for query params
  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState('name', parseAsString.withDefault(''));
  const [urlType] = useQueryState(
    'type',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlIsDefault] = useQueryState(
    'is_default',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlOffices] = useQueryState(
    'offices',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlCreatedAt] = useQueryState(
    'created_at',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<DynamicForm>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<DynamicForm>[])
  );

  const queryParams = useMemo<DynamicFormsListParams>(() => {
    const params: DynamicFormsListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    if (urlType.length) {
      params.type = urlType as DynamicFormType[];
    }
    if (urlStatus.length) {
      params.status = urlStatus as DynamicFormStatus[];
    }
    // Backend treats `is_default` as a single boolean; only forward when
    // the user picked exactly one of the two options.
    if (urlIsDefault.length === 1) {
      params.is_default = urlIsDefault[0] === '1';
    }
    if (urlOffices.length) {
      params.offices = urlOffices;
    }
    if (urlCreatedAt.length) {
      const t0 = Number(urlCreatedAt[0]);
      const t1 = Number(urlCreatedAt[1]);
      if (t0) params.created_from = new Date(t0).toISOString().split('T')[0];
      if (t1) params.created_to = new Date(t1).toISOString().split('T')[0];
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';

    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlType,
    urlStatus,
    urlIsDefault,
    urlOffices,
    urlCreatedAt,
    urlSorting
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    dynamicFormQueries.list(queryParams)
  );

  const forms = useMemo(() => (data?.data ?? []) as DynamicForm[], [data]);
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash param back to the target form for each row-scoped overlay.
  const editTarget = editOpen
    ? (forms.find((f) => String(f.id) === editId) ?? null)
    : null;
  const previewTarget = previewOpen
    ? (forms.find((f) => String(f.id) === previewId) ?? null)
    : null;
  const deleteTarget = deleteOpen
    ? (forms.find((f) => String(f.id) === deleteId) ?? null)
    : null;

  const { table } = useDataTable<DynamicForm>({
    columns,
    pageKey: 'dynamic-forms',
    data: forms,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const tableState = table.state;

  const handleCreate = useCallback(() => {
    toggleCreate(true);
  }, [toggleCreate]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }, [deleteMutation, deleteTarget, closeDelete]);

  const sortOptions = useMemo(
    () => [
      { label: t('formName'), value: 'name' },
      { label: t('status'), value: 'status' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  return (
    <div className='space-y-6'>
      {/* Page header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='text-2xl font-bold'>{t('pageTitle')}</h1>
          <p className='text-muted-foreground text-sm'>
            {t('pageDescription')}
          </p>
        </div>
        <PermissionGate permissions={['dynamic_forms.create']}>
          <Button onClick={handleCreate}>
            <Plus className='me-2 h-4 w-4' />
            {t('createNewForm')}
          </Button>
        </PermissionGate>
      </div>

      {/* Info banner */}
      <Alert className='border-primary/20 bg-primary/5'>
        <Settings className='h-4 w-4' />
        <AlertDescription>{t('infoBanner')}</AlertDescription>
      </Alert>

      {/* Metrics */}
      {isPending ? (
        <div className='grid gap-4 sm:grid-cols-3'>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className='h-24 w-full' />
          ))}
        </div>
      ) : (
        <DynamicFormMetrics forms={forms} />
      )}

      {/* View Toggle + Sort */}
      <div className='flex items-center gap-2'>
        {view !== 'table' && (
          <SortControl
            options={sortOptions}
            sorting={tableState.sorting}
            onSortingChange={(s) => table.setSorting(s)}
          />
        )}
        <DataViewToggle
          value={view}
          onValueChange={setView}
          pageKey='dynamic-forms'
        />
      </div>

      {/* Data */}
      <DataTableToolbar table={table} />

      {isPending ? (
        <ListingSkeleton view={view} />
      ) : forms.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <FileText className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noForms')}</p>
          <p className='text-muted-foreground text-sm'>
            {t('noFormsDescription')}
          </p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {forms.map((form) => (
              <DynamicFormCard
                key={form.id}
                form={form}
                onPreview={handlePreview}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDeleteTrigger}
                onToggleStatus={handleToggleStatus}
                onToggleDefault={handleToggleDefault}
                isDuplicating={duplicateMutation.isPending}
                isTogglingStatus={
                  activateMutation.isPending || deactivateMutation.isPending
                }
                isTogglingDefault={
                  setDefaultMutation.isPending || unsetDefaultMutation.isPending
                }
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {forms.map((form) => (
              <DynamicFormCompactRow
                key={form.id}
                form={form}
                onPreview={handlePreview}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDeleteTrigger}
                onToggleStatus={handleToggleStatus}
                onToggleDefault={handleToggleDefault}
                isDuplicating={duplicateMutation.isPending}
                isTogglingStatus={
                  activateMutation.isPending || deactivateMutation.isPending
                }
                isTogglingDefault={
                  setDefaultMutation.isPending || unsetDefaultMutation.isPending
                }
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <DataTable
            table={table}
            actionBar={<DynamicFormsTableActionBar table={table} />}
          />
        </div>
      )}

      {/* Wizard dialog (shared create + edit) */}
      <DynamicFormWizardDialog
        open={createOpen || (editOpen && !!editTarget)}
        onOpenChange={(open) => {
          if (!open) (editOpen ? closeEdit : toggleCreate)(false);
        }}
        editingForm={editOpen ? editTarget : null}
      />

      {/* Preview dialog */}
      <DynamicFormPreviewDialog
        open={previewOpen && !!previewTarget}
        onOpenChange={closePreview}
        form={previewTarget}
      />

      {/* Delete dialog */}
      <DeleteFormDialog
        form={deleteTarget}
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
